/**
 * builderStoreApi — a tiny READ client for the seller's store identity. Used for (a) the public
 * storefront "Preview" URL (the builder only knows the OPAQUE `store_id` JWT claim; the storefront
 * resolves by SLUG), (b) the store's SELLING COUNTRY + currency, so the canvas's `cod-form` block
 * previews the country-correct form (city source, phone example) — matching the live storefront, and
 * (c) the store's CARD readiness, so the builder never offers a Stripe checkout the store can't take.
 *
 * Reuses the authed `apiRequest` (Bearer from the JWT; the store is derived server-side — no tenant
 * surface here) and the EXISTING `GET /api/store/settings/general` endpoint — no new backend endpoint.
 * Read-only.
 */
import { apiRequest } from './http';

/** The subset of `StoreGeneralResponse` (backend `Dtos.kt`) the builder needs. */
export interface StoreIdentity {
  slug: string;
  /** The seller-facing display name — the brand context the generation prompt opens with (0059). */
  storeName: string;
  /** ISO-3166 alpha-2 selling country — drives the COD form's city source, phone example and locale. */
  countryCode: string;
  /** ISO-4217. */
  currency: string;
  /** The store's own language (fr | ar | en) — drives the canvas locale and its reading direction. */
  language: string;
  /**
   * The shop's PUBLIC address when it is a custom domain (feature 0046): the primary VERIFIED domain, or
   * null when the platform subdomain is the address. "Preview" / "View store" open it — the same door as
   * the dashboard's "view store" eye.
   */
  primaryDomain: string | null;
}

const GENERAL_PATH = '/api/store/settings/general';
const PAYMENT_MODE_PATH = '/api/store/payment-mode';

export const storeApi = {
  /** GET /api/store/settings/general — the seller's store profile; slug + selling country + currency. */
  async getIdentity(): Promise<StoreIdentity> {
    const res = await apiRequest<{
      slug: string;
      storeName: string;
      country: string;
      currency: string;
      language: string;
      primaryDomain?: string | null;
    }>(GENERAL_PATH);
    return {
      slug: res.slug,
      storeName: res.storeName,
      countryCode: res.country,
      currency: res.currency,
      language: res.language,
      primaryDomain: res.primaryDomain ?? null,
    };
  },

  /**
   * GET /api/store/payment-mode — the ONE method the store accepts (founder decision 2026-08-01).
   *
   * The builder needs it to stay HONEST: the canvas must preview the buy-box the customer really gets, and
   * the inspector must offer only the journeys that fit that method. It reads the resolved `method`, not the
   * capabilities — a store with Stripe connected that sells cash-on-delivery is a COD store here. `null` =
   * the store accepts nothing yet (or the read failed), which the canvas treats as "unknown", not as "off".
   */
  async getPaymentMethod(): Promise<'CARD' | 'COD' | null> {
    const res = await apiRequest<{ method?: string }>(PAYMENT_MODE_PATH);
    return res?.method === 'CARD' || res?.method === 'COD' ? res.method : null;
  },
};
